import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiBarChart2,
  FiCheckCircle,
  FiShield,
  FiTrendingUp,
  FiSliders,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const metrics = [
  { value: 'MT5', label: 'Direct platform workflow' },
  { value: '7 Days', label: 'Evaluation period' },
  { value: '3 Steps', label: 'From signup to setup' },
  { value: '24/7', label: 'Live account visibility' },
];
const trustSignals = [
  'Designed for MT5 traders who need a cleaner operating flow.',
  'No custody changes — funds remain with your broker.',
  'Guided setup path from signup through risk configuration.',
];

const featureCards = [
  {
    icon: FiTrendingUp,
    title: 'Structured automation',
    description: 'Run rule-based execution from one focused workflow.',
  },
  {
    icon: FiShield,
    title: 'Clear risk limits',
    description: 'Set trade caps, profit targets, and loss controls before going live.',
  },
  {
    icon: FiBarChart2,
    title: 'One account workspace',
    description: 'Monitor positions, account status, and trading activity in one place.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Create account',
    description: 'Create the account and move directly into setup.',
  },
  {
    number: '02',
    title: 'Connect MT5',
    description: 'Link your trading environment while keeping funds with your broker.',
  },
  {
    number: '03',
    title: 'Set limits and run',
    description: 'Configure symbols, trade limits, and risk rules from your account area.',
  },
];

function Homepage() {
  const { isAuthed, isLoading, subscriptionStatus } = useAuth();
  const canOpenAccount = !isLoading && isAuthed && ['active', 'lifetime', 'trial', 'trialing'].includes(subscriptionStatus);

  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.14),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.12),_transparent_28%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <section className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8 lg:pt-36">
        <div className="pointer-events-none absolute left-1/2 top-[16%] -z-10 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-cyan-300/10 blur-[110px]" />
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
              <FiCheckCircle className="h-4 w-4" />
              Structured MT5 automation
            </div>

            <h1 className="max-w-4xl text-5xl font-bold leading-[1] tracking-tight text-white sm:text-6xl lg:text-7xl">
              MT5 automation
              <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
                with clear risk control
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
              DailyProfit Trader helps you connect MT5, define trading limits, and monitor execution from one professional workflow.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              {canOpenAccount ? (
                <>
                  <Link
                    to="/account"
                    className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-400 to-cyan-400 px-7 py-4 text-base font-bold text-slate-950 transition duration-300 hover:scale-[1.02] hover:shadow-[0_18px_50px_rgba(34,211,238,0.22)]"
                  >
                    Open user account
                    <FiArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur hover:border-cyan-300/40 hover:bg-white/10"
                  >
                    View pricing
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-400 to-cyan-400 px-7 py-4 text-base font-bold text-slate-950 transition duration-300 hover:scale-[1.02] hover:shadow-[0_18px_50px_rgba(34,211,238,0.22)]"
                  >
                    Start free trial
                    <FiArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur hover:border-cyan-300/40 hover:bg-white/10"
                  >
                    View pricing
                  </Link>
                </>
              )}
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-3xl border border-cyan-200/15 bg-white/[0.07] p-5 backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:border-cyan-300/30 hover:bg-white/[0.09]"
                >
                  <div className="text-3xl font-bold text-white">{metric.value}</div>
                  <div className="mt-2 text-sm leading-6 text-slate-400">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-r from-emerald-300/20 via-cyan-300/20 to-sky-400/20 blur-lg" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950/75 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl">
                    <section className="px-4 pb-6 sm:px-6 lg:px-8">
                      <div className="mx-auto grid max-w-7xl gap-3 rounded-[1.5rem] border border-cyan-200/10 bg-white/[0.03] p-3 backdrop-blur-xl md:grid-cols-3">
                        {trustSignals.map((signal) => (
                          <div
                            key={signal}
                            className="rounded-2xl border border-white/10 bg-slate-900/65 px-4 py-3 text-sm leading-6 text-slate-300"
                          >
                            {signal}
                          </div>
                        ))}
                      </div>
                    </section>
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-cyan-200/70">Platform overview</p>
                  <h2 className="mt-2 text-2xl font-semibold text-white">One place to manage the workflow</h2>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-3 text-slate-300">
                    <FiSliders className="h-5 w-5 text-emerald-300" />
                    Configuration
                  </div>
                  <p className="mt-4 text-lg font-semibold text-white">Symbols, trade limits, and risk settings</p>
                  <p className="mt-2 text-sm text-slate-400">Set the operating rules before live execution begins.</p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-400/15 to-transparent p-4">
                  <div className="text-sm text-slate-300">Account connection</div>
                  <p className="mt-4 text-lg font-semibold text-white">MT5 linked to your broker workflow</p>
                  <p className="mt-2 text-sm text-slate-400">Funds stay with your broker while the platform manages the process.</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-white/10 bg-slate-900/80 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Onboarding</div>
                  <div className="mt-2 text-lg font-semibold text-white">Included</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-slate-900/80 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Limits</div>
                  <div className="mt-2 text-lg font-semibold text-white">Configurable</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-slate-900/80 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Monitoring</div>
                  <div className="mt-2 text-lg font-semibold text-white">Live account view</div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 bg-gradient-to-r from-white/5 to-transparent p-4 text-sm text-slate-300">
                <div className="flex items-center gap-3">
                  <FiCheckCircle className="h-4 w-4 text-cyan-300" />
                  Clear onboarding and visible controls in one workflow.
                </div>
                <Link
                  to="/#how-it-works"
                  className="font-semibold text-cyan-200 transition hover:text-white"
                >
                  View steps
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-200/70">Core capabilities</p>
              <h2 className="mt-3 text-4xl font-semibold text-white sm:text-5xl">What the platform helps you do</h2>
            </div>
            <p className="max-w-2xl text-slate-400">
              The focus is simple: configure the rules, connect the account, and monitor performance without unnecessary complexity.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {featureCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="group rounded-[1.75rem] border border-white/10 bg-slate-950/70 p-6 transition duration-300 hover:-translate-y-1 hover:border-cyan-300/30 hover:bg-slate-900/80"
                >
                  <div className="inline-flex rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-cyan-200">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-white">{card.title}</h3>
                  <p className="mt-3 leading-7 text-slate-400">{card.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(16,185,129,0.08),rgba(14,165,233,0.03),rgba(255,255,255,0.02))] p-6 backdrop-blur-xl lg:p-10">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-emerald-200/70">How it works</p>
              <h2 className="mt-4 text-4xl font-semibold text-white sm:text-5xl">A short path from signup to setup</h2>
              <p className="mt-4 max-w-xl text-lg leading-8 text-slate-300">
                Create the account, connect MT5, and set your limits in one guided workflow before moving into live use.
              </p>
            </div>

            <div className="grid gap-4">
              {steps.map((step) => (
                <div key={step.number} className="grid gap-4 rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-5 md:grid-cols-[auto_1fr] md:items-start">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-300 to-cyan-300 text-lg font-black text-slate-950">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                    <p className="mt-2 leading-7 text-slate-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-24 pt-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] border border-white/10 bg-[linear-gradient(135deg,#0f1722_0%,#0f2c33_55%,#093340_100%)] p-8 shadow-[0_30px_100px_rgba(0,0,0,0.35)] lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-200/70">Start here</p>
              <h2 className="mt-4 text-4xl font-semibold text-white sm:text-5xl">Start with the platform and continue only if the workflow fits.</h2>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-300">
                Create one account, connect MT5, and configure your rules. Registration includes a 7-day trial before you choose billing.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              {canOpenAccount ? (
                <Link
                  to="/account"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-4 font-bold text-slate-950 transition hover:scale-[1.02]"
                >
                  Open user account
                  <FiArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-4 font-bold text-slate-950 transition hover:scale-[1.02]"
                  >
                    Create account
                    <FiArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-7 py-4 font-semibold text-white backdrop-blur hover:bg-white/10"
                  >
                    Sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Homepage;
